import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Header,
  SpaceBetween,
  Form,
  FormField,
  Input,
  Toggle,
  Select,
  Button,
  ExpandableSection,
  Alert,
  Tabs,
  TextContent,
  Tiles,
  ColumnLayout,
  Spinner
} from '@cloudscape-design/components';
import { useConfig, ConfigSettings } from '../hooks/useConfig';
import { useHelpPanel } from '../contexts/HelpContext';
import { InfoLink } from '../components/help/common';
import { ConfigurationHelp } from '../components/help/ConfigurationHelp';

interface ConfigurationProps {
  addNotification: (notification: any) => void;
}

const Configuration: React.FC<ConfigurationProps> = ({ addNotification }) => {
  const [activeTabId, setActiveTabId] = useState('general');
  const { setContent } = useHelpPanel();

  const {
    config,
    loading,
    error,
    testResults,
    testLoading,
    updateConfig,
    testConnections
  } = useConfig();

  // Local state to track form changes
  const [isDevelopmentMode, setIsDevelopmentMode] = useState(true);
  const [settings, setSettings] = useState<ConfigSettings>({
    developmentEmail: 'test@example.com',
    slackWebhookUrl: 'https://hooks.slack.com/services/xxxx/yyyy/zzzzz',
    daysBeforeEscalation: '3',
    sesConfigurationSet: 'EmailTrackingConfigSet',
    sourceEmail: 'noreply@example.com',
    isDevelopmentMode: true,
    useHtmlEmails: false,
    domainOwnerEmail: 'aws-solutions@amazon.com',
    domainOwnerFirstName: 'AWS',
    domainOwnerLastName: 'Solutions',
    domainOwnerTitle: 'Solutions Architect',
    domainOwnerPhone: '1-800-AWS-HELP',
    domainOwnerAlias: 'aws-solutions'
  });

  // Update local state when config is loaded
  useEffect(() => {
    if (config) {
      setSettings(config);
      setIsDevelopmentMode(config.isDevelopmentMode);
    }
  }, [config]);

  const handleSaveSettings = async () => {
    // Create updated settings object with current development mode
    const updatedSettings = {
      ...settings,
      isDevelopmentMode
    };

    addNotification({
      type: 'info',
      content: 'Saving settings...',
      dismissible: true
    });

    const success = await updateConfig(updatedSettings);

    if (success) {
      addNotification({
        type: 'success',
        content: 'Settings have been saved successfully.',
        dismissible: true,
        dismissTimeout: 5000
      });
    } else {
      addNotification({
        type: 'error',
        content: 'Failed to save settings. Please try again.',
        dismissible: true
      });
    }
  };

  const handleTestConnection = async () => {
    addNotification({
      type: 'info',
      content: 'Testing connections...',
      dismissible: true
    });

    const results = await testConnections();

    if (results && results.success !== false) {
      // Check individual connection results
      const allSuccessful = results.redshift && results.ses && results.slack;
      const partialSuccess = results.redshift || results.ses || results.slack;

      if (allSuccessful) {
        addNotification({
          type: 'success',
          content: 'All connections tested successfully.',
          dismissible: true,
          dismissTimeout: 5000
        });
      } else if (partialSuccess) {
        addNotification({
          type: 'warning',
          content: 'Some connections failed. Check details for more information.',
          dismissible: true
        });
      } else {
        addNotification({
          type: 'error',
          content: 'All connections failed. Please check your settings.',
          dismissible: true
        });
      }
    } else {
      addNotification({
        type: 'error',
        content: `Connection test failed: ${results?.error || 'Unknown error'}`,
        dismissible: true
      });
    }
  };

  return (
    <Box padding="l">
      <SpaceBetween size="l">
        <Header
          variant="h1"
          info={<InfoLink onFollow={() => setContent(<ConfigurationHelp />)} />}
          actions={
            loading && <Spinner size="normal" />
          }
        >
          Configuration
        </Header>

        {error && (
          <Alert
            type="error"
            header="Error loading configuration"
            dismissible
          >
            {error.message}
          </Alert>
        )}

        <Tabs
          activeTabId={activeTabId}
          onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
          tabs={[
            {
              id: 'general',
              label: 'General Settings',
              content: (
                <Container>
                  <Form>
                    <SpaceBetween size="l">
                      <FormField
                        label="Environment Mode"
                        description="Toggle between development and production environments"
                      >
                        <Toggle
                          onChange={({ detail }) => setIsDevelopmentMode(detail.checked)}
                          checked={isDevelopmentMode}
                        >
                          Development Mode {isDevelopmentMode ? 'On' : 'Off'}
                        </Toggle>
                      </FormField>

                      {isDevelopmentMode && (
                        <Alert type="info">
                          Development mode is enabled. Emails will be sent to the development email address,
                          and additional logging will be activated.
                        </Alert>
                      )}

                      <FormField
                        label="Development Email"
                        description="Email to use for testing in development mode"
                        constraintText="Required in development mode"
                      >
                        <Input
                          value={settings.developmentEmail}
                          onChange={({ detail }) =>
                            setSettings(prev => ({ ...prev, developmentEmail: detail.value }))
                          }
                          placeholder="test@example.com"
                          disabled={!isDevelopmentMode}
                        />
                      </FormField>

                      <FormField
                        label="Development Slack Alias"
                        description="Slack user ID to use for testing in development mode"
                        constraintText="Required in development mode for Slack notifications"
                      >
                        <Input
                          value={settings.developmentSlackAlias || ""}
                          onChange={({ detail }) =>
                            setSettings(prev => ({ ...prev, developmentSlackAlias: detail.value }))
                          }
                          placeholder="slack_user_id"
                          disabled={!isDevelopmentMode}
                        />
                      </FormField>

                      <FormField
                        label="Source Email"
                        description="Email address used as the sender for all notifications"
                        constraintText="Must be verified in SES"
                      >
                        <Input
                          value={settings.sourceEmail}
                          onChange={({ detail }) =>
                            setSettings(prev => ({ ...prev, sourceEmail: detail.value }))
                          }
                          placeholder="noreply@example.com"
                        />
                      </FormField>

                      <FormField
                        label="Days Before Escalation"
                        description="Number of days to wait before escalating unread notifications to Slack"
                      >
                        <Input
                          value={settings.daysBeforeEscalation}
                          onChange={({ detail }) =>
                            setSettings(prev => ({ ...prev, daysBeforeEscalation: detail.value }))
                          }
                          type="number"
                          placeholder="3"
                        />
                      </FormField>

                      <FormField
                        label="Email Format"
                        description="Choose between HTML formatted emails or plain text emails"
                      >
                        <Toggle
                          onChange={({ detail }) =>
                            setSettings(prev => ({ ...prev, useHtmlEmails: detail.checked }))
                          }
                          checked={settings.useHtmlEmails || false}
                        >
                          HTML Emails {settings.useHtmlEmails ? 'Enabled' : 'Disabled'}
                        </Toggle>
                      </FormField>

                      {!settings.useHtmlEmails && (
                        <Alert type="info">
                          Plain text mode is enabled. Emails will be sent as simple text without HTML formatting.
                        </Alert>
                      )}



                      <ExpandableSection header="Domain Owner Information">
                        <SpaceBetween size="l">
                          <FormField
                            label="Domain Owner Email"
                            description="Email address of the domain owner (used in email signatures)"
                          >
                            <Input
                              value={settings.domainOwnerEmail || ''}
                              onChange={({ detail }) =>
                                setSettings(prev => ({ ...prev, domainOwnerEmail: detail.value }))
                              }
                              placeholder="owner@example.com"
                            />
                          </FormField>

                          <ColumnLayout columns={2}>
                            <FormField
                              label="First Name"
                              description="Domain owner's first name"
                            >
                              <Input
                                value={settings.domainOwnerFirstName || ''}
                                onChange={({ detail }) =>
                                  setSettings(prev => ({ ...prev, domainOwnerFirstName: detail.value }))
                                }
                                placeholder="John"
                              />
                            </FormField>

                            <FormField
                              label="Last Name"
                              description="Domain owner's last name"
                            >
                              <Input
                                value={settings.domainOwnerLastName || ''}
                                onChange={({ detail }) =>
                                  setSettings(prev => ({ ...prev, domainOwnerLastName: detail.value }))
                                }
                                placeholder="Doe"
                              />
                            </FormField>
                          </ColumnLayout>

                          <FormField
                            label="Title"
                            description="Domain owner's job title"
                          >
                            <Input
                              value={settings.domainOwnerTitle || ''}
                              onChange={({ detail }) =>
                                setSettings(prev => ({ ...prev, domainOwnerTitle: detail.value }))
                              }
                              placeholder="Solutions Architect"
                            />
                          </FormField>

                          <ColumnLayout columns={2}>
                            <FormField
                              label="Phone"
                              description="Domain owner's phone number"
                            >
                              <Input
                                value={settings.domainOwnerPhone || ''}
                                onChange={({ detail }) =>
                                  setSettings(prev => ({ ...prev, domainOwnerPhone: detail.value }))
                                }
                                placeholder="+1-555-123-4567"
                              />
                            </FormField>

                            <FormField
                              label="Slack Alias"
                              description="Domain owner's Slack alias"
                            >
                              <Input
                                value={settings.domainOwnerAlias || ''}
                                onChange={({ detail }) =>
                                  setSettings(prev => ({ ...prev, domainOwnerAlias: detail.value }))
                                }
                                placeholder="jdoe"
                              />
                            </FormField>
                          </ColumnLayout>
                        </SpaceBetween>
                      </ExpandableSection>

                      <ExpandableSection header="Advanced AWS Settings">
                        <SpaceBetween size="l">
                          <FormField
                            label="SES Configuration Set"
                            description="SES configuration set for email tracking"
                          >
                            <Input
                              value={settings.sesConfigurationSet}
                              onChange={({ detail }) =>
                                setSettings(prev => ({ ...prev, sesConfigurationSet: detail.value }))
                              }
                              placeholder="EmailTrackingConfigSet"
                            />
                          </FormField>
                        </SpaceBetween>
                      </ExpandableSection>
                    </SpaceBetween>
                  </Form>
                </Container>
              )
            },
            {
              id: 'integrations',
              label: 'Integrations',
              content: (
                <Container header={<Header>External Integrations</Header>}>
                  <SpaceBetween size="l">
                    <FormField
                      label="Slack Webhook URL"
                      description="Webhook URL for Slack notifications"
                      constraintText="Required for Slack notifications"
                    >
                      <Input
                        value={settings.slackWebhookUrl}
                        onChange={({ detail }) =>
                          setSettings(prev => ({ ...prev, slackWebhookUrl: detail.value }))
                        }
                        placeholder="https://hooks.slack.com/services/xxxx/yyyy/zzzzz"
                      />
                    </FormField>
                  </SpaceBetween>
                </Container>
              )
            },
            {
              id: 'infrastructure',
              label: 'Infrastructure Info',
              content: (
                <SpaceBetween size="l">
                  <Container header={<Header>Database Connections</Header>}>
                    <TextContent>

                      <p>
                        <strong>PostgreSQL Application Database:</strong> The PostgreSQL database is managed automatically
                        by the CDK stack. Database credentials are stored in AWS Secrets Manager and accessed securely
                        by the Lambda functions.
                      </p>
                    </TextContent>

                    <SpaceBetween direction="horizontal" size="xs">
                      <Button iconName="external" href="https://us-east-1.console.aws.amazon.com/rds/home?region=us-east-1#database:id=canarydev-postgresqlinstance88d67abc-zvyfyqs31w96;is-cluster=false" target="_blank">
                        View RDS in AWS Console
                      </Button>
                    </SpaceBetween>
                  </Container>

                  <Container header={<Header>Configuration Architecture</Header>}>
                    <TextContent>
                      <p>
                        This application uses a hybrid configuration approach:
                      </p>
                      <ul>
                        <li><strong>Application Settings:</strong> Managed through this UI and stored in DynamoDB</li>
                        <li><strong>Infrastructure Settings:</strong> Managed via CDK deployment and AWS services</li>
                        <li><strong>Secrets:</strong> Stored securely in AWS Secrets Manager</li>
                      </ul>
                      <p>
                        Changes to application settings take effect immediately. Infrastructure changes require
                        redeployment of the CDK stack.
                      </p>
                    </TextContent>
                  </Container>
                </SpaceBetween>
              )
            }
          ]}
        />
      </SpaceBetween>
      <SpaceBetween direction="horizontal" size="xs" alignItems='end'>
        <Button onClick={handleTestConnection} loading={testLoading}>Test Connections</Button>
        <Button variant="primary" onClick={handleSaveSettings} loading={loading}>
          Save Settings
        </Button>
      </SpaceBetween>
    </Box>
  );
};

export default Configuration;
